import React, { useState, useEffect, useRef, useCallback } from "react";
import { loadArticles } from "../../../api/articles";
import { Link } from "react-router-dom";
import { truncateAtLastSpace } from "../../../utils/textUtils";
import ArticleCardSkeleton from "./ArticleCardSkeleton";
import ExternalArticleModal from "./ExternalArticleModal";
import "../../../assets/css/animate.css";
import "./articles.css";

const Articles = () => {
  const MAX_DISPLAY_AGE_DAYS = 30;
  const getSourceLabel = (source) => source || "Externe";
  const getSourceBadgeClass = (source) => {
    const normalized = String(source || "").toLowerCase();
    if (normalized === "islamweb") return "source-islamweb";
    if (normalized === "islamhouse") return "source-islamhouse";
    return "source-default";
  };

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [externalModal, setExternalModal] = useState({ isOpen: false, url: "", title: "" });
  const [showCategoryScrollHint, setShowCategoryScrollHint] = useState(false);
  const categoriesRef = useRef(null);

  const filterRecentByDisplayDate = useCallback((items) => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - MAX_DISPLAY_AGE_DAYS);
    return (items || []).filter((article) => {
      const rawDate = new Date(article.date);
      return !Number.isNaN(rawDate.getTime()) && rawDate >= cutoff;
    });
  }, [MAX_DISPLAY_AGE_DAYS]);

  const loadLastArticles = useCallback(async () => {
    try {
      // Disable cache here so page content is not limited by home-page cache size.
      const articles = await loadArticles(false, 0);
      const filteredArticles = filterRecentByDisplayDate(articles);
      setArticles(filteredArticles);
      setCategories([...new Set(filteredArticles.map((article) => article.category))].sort());
    } catch (error) {
      console.error("Failed to load articles:", error);
    } finally {
      setLoading(false);
    }
  }, [filterRecentByDisplayDate]);

  useEffect(() => {
    loadLastArticles();
  }, [loadLastArticles]);

  const scrollTop = () => { (window.scrollTo({ top: 0, behavior: "smooth" })) };

  const displayedArticles = activeCategory
    ? articles.filter(a => a.category === activeCategory)
    : articles;

  const getBackgroundColor = (index) => {
    const colors = [
      'rgba(47, 172, 102, 0.85)',   // vert
      'rgba(206, 20, 70, 0.85)',    // rouge
      'rgba(47, 137, 252, 0.85)',   // bleu
      'rgba(255, 153, 0, 0.85)',    // orange
      'rgba(156, 39, 176, 0.85)',   // violet
      'rgba(0, 150, 136, 0.85)',    // turquoise
      'rgba(233, 30, 99, 0.85)',    // rose
      'rgba(255, 193, 7, 0.85)',    // jaune
      'rgba(103, 58, 183, 0.85)',   // indigo
      'rgba(96, 125, 139, 0.85)'    // gris bleuté
    ];
    return colors[index % 10];
  };

  const openExternalArticle = (article) => {
    if (!article?.sourceUrl) return;
    setExternalModal({
      isOpen: true,
      url: article.sourceUrl,
      title: article.title,
    });
  };

  const closeExternalArticle = () => {
    setExternalModal({ isOpen: false, url: "", title: "" });
  };

  const updateCategoryScrollHint = () => {
    const el = categoriesRef.current;
    if (!el) {
      setShowCategoryScrollHint(false);
      return;
    }

    const canScroll = el.scrollWidth > el.clientWidth + 4;
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    setShowCategoryScrollHint(canScroll && !atEnd);
  };

  const scrollCategoriesRight = () => {
    const el = categoriesRef.current;
    if (!el) return;
    el.scrollBy({ left: 180, behavior: "smooth" });
  };

  useEffect(() => {
    if (loading) return undefined;

    const refreshHint = () => updateCategoryScrollHint();

    // Re-check after paint and shortly after to catch late layout/font shifts.
    const rafId = window.requestAnimationFrame(refreshHint);
    const timeoutId = window.setTimeout(refreshHint, 180);

    refreshHint();
    window.addEventListener("resize", refreshHint);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", refreshHint);
    };
  }, [categories, activeCategory, loading]);

  return (
    <>
      {/* =============== Blog area start =============== */}
      <div className="blog-grid-wrapper ">
        <div className="container position-relative pt-110">
          <div className="row">
            <div className="col-lg-12 ">
              <div className="section-head">
                <h3>Derniers articles</h3>
              </div>
            </div>
          </div>

          {loading ? (
            // Skeleton loading state
            <div className="article row">
              {[...Array(6)].map((_, index) => (
                <ArticleCardSkeleton key={`skeleton-${index}`} />
              ))}
            </div>
          ) : (
            <>
              <div className="row">
                <div className="col-lg-12">
                  <div className={`articles-filter blog-category-wrap ${showCategoryScrollHint ? "has-scroll-hint" : ""}`}>
                    <ul className="categoryes" ref={categoriesRef} onScroll={updateCategoryScrollHint}>
                      <li>
                        <button
                          type="button"
                          className={`category-link ${activeCategory === null ? 'active' : ''}`}
                          aria-pressed={activeCategory === null}
                          onClick={() => setActiveCategory(null)}
                        >
                          Tous
                        </button>
                      </li>
                      {categories.map((cat, i) => (
                        <li key={i}>
                          <button
                            type="button"
                            className={`category-link ${activeCategory === cat ? 'active' : ''}`}
                            aria-pressed={activeCategory === cat}
                            onClick={() => setActiveCategory(cat)}
                          >
                            {cat}
                          </button>
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className="category-scroll-hint"
                      onClick={scrollCategoriesRight}
                      aria-label="Voir plus de categories"
                    >
                      <i className="bi bi-chevron-right" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="article row">
            {displayedArticles.map((article, index) => (
              <div key={article.id}
                className="col-lg-4 col-md-6 wow fadeInUp animated"
                data-wow-delay="200ms"
                data-wow-duration="1500ms">
                {article.isExternal ? (
                  <button
                    type="button"
                    className="article-card-link"
                    onClick={() => openExternalArticle(article)}
                    style={{ border: "none", background: "transparent", padding: 0, width: "100%", textAlign: "inherit" }}
                  >
                    <div className="article-card-md">
                      <div className="article-thumb">
                        <div className="article-date-badge">
                          <span className="date-day">{new Date(article.date).getDate()}</span>
                          <span className="date-month">{new Date(article.date).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}</span>
                        </div>

                        <div className={`article-source-badge ${getSourceBadgeClass(article.source)}`}>{getSourceLabel(article.source)}</div>

                        <img className="article-image" src={article.image} alt={article.title} />

                        <div className="article-category-block" style={{ background: getBackgroundColor(index) }}>
                          <i className="bi bi-bookmark-fill" />
                          <span>{article.category}</span>
                        </div>
                      </div>

                      <div className="article-content">
                        <h4 className="article-content-title">{truncateAtLastSpace(article.title || "Sans titre", 80)}</h4>
                        <div className="article-footer">
                          <span className="article-read-more">
                            Lire la source <i className="bi bi-box-arrow-up-right"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ) : (
                  <Link
                    onClick={scrollTop}
                    to={`${process.env.PUBLIC_URL}/article-details?aid=${article.id}`}
                    className="article-card-link"
                  >
                    <div className="article-card-md">
                      <div className="article-thumb">
                        <div className="article-date-badge">
                          <span className="date-day">{new Date(article.date).getDate()}</span>
                          <span className="date-month">{new Date(article.date).toLocaleDateString('fr-FR', { month: 'short' }).toUpperCase()}</span>
                        </div>

                        <div className={`article-source-badge ${getSourceBadgeClass(article.source)}`}>{getSourceLabel(article.source)}</div>

                        <img className="article-image" src={article.image} alt={article.title} />

                        <div className="article-category-block" style={{ background: getBackgroundColor(index) }}>
                          <i className="bi bi-bookmark-fill" />
                          <span>{article.category}</span>
                        </div>
                      </div>

                      <div className="article-content">
                        <h4 className="article-content-title">{truncateAtLastSpace(article.title || "Sans titre", 80)}</h4>
                        <div className="article-footer">
                          <span className="article-read-more">
                            Lire l'article <i className="bi bi-arrow-right"></i>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                )}
              </div>
            ))}
              
            </div>
            </>
          )}
        </div>
      </div>
      <ExternalArticleModal
        isOpen={externalModal.isOpen}
        url={externalModal.url}
        title={externalModal.title}
        onClose={closeExternalArticle}
      />
      {/* =============== Blog area end =============== */}
    </>
  );
};

export default Articles;
